import { donutSegments, type DonutGeometry } from '@/components/ui/donut-geometry';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/cn';

/** One wedge of the ring, and the legend row that carries its figure as text. */
export interface DonutSlice {
  label: string;
  value: number;
  /**
   * A text-colour utility built on a token - `text-danger`, `text-tag-teal`.
   * The arc paints itself with `currentColor` and the legend swatch with
   * `bg-current`, so one class draws both and the two cannot drift apart.
   */
  className: string;
  /**
   * Opens whatever lists the records behind this wedge.
   *
   * Absent when there is nothing to open, and the row then says so by being
   * plainly inert - no hover, no pointer, no tab stop. The legend used to
   * mix rows that were links with rows that were bare text and drew them
   * identically, so the only way to find out which was which was to click.
   */
  onSelect?: () => void;
}

export interface DonutChartProps {
  /** What the ring divides up, as a screen reader hears it first. */
  title: string;
  /** Exclusive shares of one whole. See `donutSegments` on why that matters. */
  slices: DonutSlice[];
  /** The figure in the hole. Always the whole the wedges are shares of. */
  centreValue: number | string;
  centreLabel: string;
  /** Said in place of the legend when there is nothing to divide up. */
  emptyMessage: string;
  /** Outer diameter, in pixels. */
  size?: number;
  className?: string;
}

/**
 * A ring of exclusive shares, with the numbers beside it in words.
 *
 * The chart is never the only way to read the figures: the legend states each
 * one as text, the `aria-label` reads the whole thing out as a sentence, and
 * both survive the cases the colours do not - a printer, a projector, or a
 * reader who cannot tell the amber from the red.
 */
export function DonutChart({
  title,
  slices,
  centreValue,
  centreLabel,
  emptyMessage,
  size = 124,
  className,
}: DonutChartProps) {
  const geometry: DonutGeometry = {
    radius: size / 2,
    thickness: Math.round(size * 0.22),
    centre: size / 2,
    gap: 2,
  };

  const wedges = donutSegments(slices, geometry);
  const total = slices.reduce((sum, slice) => sum + Math.max(slice.value, 0), 0);

  const spoken =
    total > 0 ? slices.map((slice) => `${slice.label}: ${slice.value}`).join(', ') : emptyMessage;

  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-3', className)}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          role="img"
          aria-label={`${title}. ${centreValue} ${centreLabel}. ${spoken}`}
        >
          {/* Drawn whether or not there is anything to divide up, so a brand
              new tenant gets a complete, quiet ring rather than a gap where a
              chart is supposed to be. */}
          <circle
            cx={geometry.centre}
            cy={geometry.centre}
            r={geometry.radius - geometry.thickness / 2}
            fill="none"
            strokeWidth={geometry.thickness}
            className="stroke-chart-track"
          />

          {wedges.map((wedge) =>
            wedge.path === null ? null : (
              <path
                key={wedge.label}
                d={wedge.path}
                fill="currentColor"
                className={wedge.className}
              />
            ),
          )}
        </svg>

        {/* The same figures are already in the `aria-label`, so to a screen
            reader this is decoration; without it they are read out twice. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
        >
          <span className="text-xl leading-none font-bold text-content tabular-nums">
            {centreValue}
          </span>
          <span className="mt-1 text-2xs leading-none tracking-wide text-content-muted uppercase">
            {centreLabel}
          </span>
        </div>
      </div>

      {total === 0 ? (
        <p className="min-w-36 flex-1 text-xs text-content-muted">{emptyMessage}</p>
      ) : (
        <ul className="min-w-36 flex-1 space-y-0.5">
          {wedges.map((wedge) => (
            <li key={wedge.label}>
              <DonutLegendRow slice={wedge} percent={wedge.percent} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One figure from the ring, as a line of text.
 *
 * A row that opens something is a button, and says so before it is pressed:
 * the chevron is there at rest rather than on hover, because hover is not an
 * answer on a phone and the reader should not have to click a row to find
 * out whether clicking it does anything.
 */
function DonutLegendRow({ slice, percent }: { slice: DonutSlice; percent: number }) {
  const row = (
    <>
      <span
        aria-hidden="true"
        className={cn('size-2 shrink-0 rounded-xs bg-current', slice.className)}
      />
      <span className="min-w-0 flex-1 truncate text-2xs text-content-muted">{slice.label}</span>
      {/* Figure, share and chevron ride together on a tight gap rather than
          on the row's own. At 390px the legend is 66px wide and "Within 30
          days" wants 72 - the four pixels a wide gap takes on either side of
          the percentage are the difference between the label reading and the
          label ending in an ellipsis. */}
      <span className="flex shrink-0 items-center gap-1">
        <span className="text-xs font-semibold text-content tabular-nums">{slice.value}</span>
        <span className="w-8 text-right text-2xs text-content-subtle tabular-nums">
          {Math.round(percent * 100)}%
        </span>
        {slice.onSelect && <Icon name="chevron-right" size={11} className="text-content-subtle" />}
      </span>
    </>
  );

  const className = 'flex w-full items-center gap-2 rounded-xs px-1 py-1 text-left';

  if (!slice.onSelect) {
    return <span className={className}>{row}</span>;
  }

  return (
    <button
      type="button"
      onClick={slice.onSelect}
      className={cn(
        className,
        'cursor-pointer transition-colors duration-quick hover:bg-surface-muted',
      )}
    >
      {row}
    </button>
  );
}
